import { makeStyles } from '@paintscout/ui'
import type { Action, MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions, useUserPermissions } from '@paintscout/ui-business'
import AddBoxIcon from '@material-ui/icons/AddBox'
import ArchiveIcon from '@material-ui/icons/Archive'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import React, { useMemo } from 'react'
import type { RenderableItem } from '@paintscout/util/builder'
import { getItems, getObjectLabels, getQuoteOptions } from '@paintscout/util/builder'
import { canSaveItemToSettings, saveToSettingsAction } from '../util/saveToSettingsAction'
import { useQuote } from '../context'
import ItemRowContent from './ItemRowContent'
import OutlinedItemTable from './OutlinedItemTable'
import type { OutlinedItemTableProps } from './OutlinedItemTable'

const ADDED_OPTIONS_SECTION = 'added-options' as const

export interface OptionsAddedTableNextProps {
  selectedItems?: OutlinedItemTableProps['selectedItems']
  onItemSelection?: OutlinedItemTableProps['onItemSelection']
  onItemAction: (action: string, section: typeof ADDED_OPTIONS_SECTION, item: RenderableItem) => void
}

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      marginTop: theme.spacing(3)
    }
  }),
  { name: 'OptionsAddedTableNext' }
)

/**
 * The "Options Added / Accepted" section for the new (estimateNext) pipeline, ported from
 * `OptionsAddedTable` onto the shared outlined primitives.
 */
export default function OptionsAddedTableNext({
  onItemAction,
  onItemSelection,
  selectedItems
}: OptionsAddedTableNextProps) {
  const classes = useStyles()
  const { quote, tableView, onReorder, isEditable, isNoteEditable } = useQuote()
  const { options } = useClientOptions()
  const { permissionToEditAccountSettings } = useUserPermissions()
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const { hideAllPrices } = getQuoteOptions({ quote, options })

  const items: RenderableItem[] = useMemo(
    () =>
      getItems({
        quote,
        section: ADDED_OPTIONS_SECTION,
        view: tableView,
        consumer: 'customer',
        options,
        overrideOptions: { showPrices: true }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quote, tableView, options]
  )

  if (items.length === 0) {
    return null
  }

  function handleReorder({ items, addIndex }) {
    onReorder({ items, section: ADDED_OPTIONS_SECTION, view: tableView, addIndex })
  }

  const useCheckboxes = !!(isEditable && onItemSelection)

  return (
    <div className={classes.root}>
      <OutlinedItemTable
        sectionTitle={`${objectLabels.option.plural} ${
          quote.status.value === 'accepted' || quote.is_invoice ? 'Accepted' : 'Added'
        }`}
        headerVariant="light"
        title={'Item'}
        draggable={!!isEditable}
        checkboxes={useCheckboxes}
        selectedItems={selectedItems}
        items={items}
        section={ADDED_OPTIONS_SECTION}
        onItemClick={
          isEditable || isNoteEditable
            ? (_ev, item) => onItemAction('edit', ADDED_OPTIONS_SECTION, item as RenderableItem)
            : undefined
        }
        onReorder={handleReorder}
        onItemSelection={onItemSelection}
        renderRowColumns={(item: RenderableItem) => {
          const actions: Action[] = [
            { key: 'edit', label: 'Edit', icon: EditIcon },
            { key: 'make-option', label: `Make ${objectLabels.option.value}`, icon: AddBoxIcon },
            { key: 'archive', label: 'Archive', icon: ArchiveIcon }
          ]

          if (canSaveItemToSettings({ permissionToEditAccountSettings, item, isEditable })) {
            actions.push(saveToSettingsAction)
          }

          // Last, as the one destructive action — the prototype puts Delete at the foot of every
          // item menu (ENG-1922). The "Are you sure?" prompt comes from the context:
          // `handleItemAction('delete')` opens a ConfirmationDialog before removing anything.
          actions.push({ key: 'delete', label: 'Delete', icon: DeleteIcon })

          return (
            <ItemRowContent
              item={item}
              section={ADDED_OPTIONS_SECTION}
              isEditable={isEditable || isNoteEditable}
              showAmount={!hideAllPrices}
              actions={isEditable ? actions : undefined}
              onAction={(action, it) => onItemAction(action, ADDED_OPTIONS_SECTION, it)}
            />
          )
        }}
      />
    </div>
  )
}
